import { Component, Inject, OnDestroy } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { Subscription } from 'rxjs';
import { CountryISO, PhoneNumberFormat } from 'ngx-intl-tel-input';
import { CurrencyFormatterService } from 'src/app/shared/services/currency-formatter.service';
import { CountryAddressService } from 'src/app/shared/services/country-address.service';
import { DataService } from 'src/app/shared/services/data.service';
import { AddressOption } from 'src/app/shared/models/country-address';
import { NotificationService } from 'src/app/utils/notification.service';
import { Vendor, VendorService } from 'src/app/pages/loan-management/services/vendor.service';

export interface VendorDealer extends Vendor {
  id?: number;
}

@Component({
  selector: 'app-vendor-dealer-dialog',
  templateUrl: './vendor-dealer-dialog.component.html',
  styleUrls: ['./vendor-dealer-dialog.component.css'],
})
export class VendorDealerDialogComponent implements OnDestroy {
  public vendorForm: FormGroup;
  public readonly newCategoryControl = new FormControl('');
  public readonly PhoneNumberFormat = PhoneNumberFormat;
  public readonly preferredCountries: CountryISO[] = [
    CountryISO.Nigeria,
    CountryISO.UnitedStates,
  ];

  /* Country -> State -> Town/City and the bank list all come from the
     country-data API, the same source the client creation forms use, so a
     tenant set up for another country gets its own lists here too. The selects
     hold codes; the API stores names, so buildPayload converts on the way out
     and hydrate matches by name on the way back in. */
  public countries: AddressOption[] = [];
  public states: AddressOption[] = [];
  public cities: AddressOption[] = [];
  public banks: AddressOption[] = [];

  /* Search boxes for the four lists. The filterOptions pipe reads these
     straight off the control, so no valueChanges plumbing is needed. */
  public readonly countrySearch = new FormControl('');
  public readonly stateSearch = new FormControl('');
  public readonly citySearch = new FormControl('');
  public readonly bankSearch = new FormControl('');

  /** true while the save request is in flight, so Save cannot double-fire */
  public saving = false;

  private subscriptions = new Subscription();

  constructor(
    private fb: FormBuilder,
    private dialogRef: MatDialogRef<VendorDealerDialogComponent>,
    public currencyFormatter: CurrencyFormatterService,
    private countryAddress: CountryAddressService,
    private data: DataService,
    private vendorService: VendorService,
    private toast: NotificationService,
    @Inject(MAT_DIALOG_DATA) public dialogData: { vendor?: VendorDealer }
  ) {
    this.dialogRef.disableClose = true;
    const vendor = dialogData?.vendor;

    this.vendorForm = this.fb.group({
      name: [vendor?.name ?? '', Validators.required],
      businessRegistrationNumber: [vendor?.businessRegistrationNumber ?? ''],
      country: [''],
      state: [{ value: '', disabled: true }],
      townCity: [{ value: '', disabled: true }],
      businessAddress: [vendor?.businessAddress ?? ''],
      contactPerson: [vendor?.contactPerson ?? ''],
      telephone: [vendor?.telephone ?? ''],
      email: [vendor?.email ?? '', Validators.email],
      // The select binds the bank code; bankName follows whichever is picked.
      bankCode: [vendor?.bankCode ?? ''],
      bankName: [vendor?.bankName ?? ''],
      // Checked only when filled - NUBAN account numbers are 10 digits.
      accountNumber: [vendor?.accountNumber ?? '', Validators.pattern(/^\d{10}$/)],
      accountName: [vendor?.accountName ?? ''],
      assetCategories: [[...(vendor?.assetCategories ?? [])]],
    });

    this.loadCountries();
    this.loadBanks();
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  /* --------------------------------------------------- country / state / city */

  private loadCountries(): void {
    this.subscriptions.add(
      this.countryAddress.getCountries().subscribe((countries) => {
        this.countries = countries;
        this.applySavedCountry();
      })
    );
  }

  /* An existing vendor stores country/state/city as names, so each level is
     matched back to its code once that level's list has arrived. A new vendor
     falls back to the tenant's own country. */
  private applySavedCountry(): void {
    const savedName = this.dialogData?.vendor?.country;
    const saved = this.byName(this.countries, savedName);

    if (saved) {
      this.vendorForm.get('country').setValue(saved.code);
      this.loadStates(saved.code);
      return;
    }

    this.subscriptions.add(
      this.data.getOrganization().subscribe((res: any) => {
        const code = res?.data?.tenantCountry?.countryCode || 'NGA';
        const control = this.vendorForm.get('country');
        if (control.value) return;
        control.setValue(code);
        this.loadStates(code);
      })
    );
  }

  private loadStates(countryCode: string): void {
    if (!countryCode) return;
    this.subscriptions.add(
      this.countryAddress.getStates(countryCode).subscribe((states) => {
        this.states = states;
        const control = this.vendorForm.get('state');
        control.enable({ emitEvent: false });

        const saved = this.byName(states, this.dialogData?.vendor?.state);
        if (!saved || control.value) return;
        control.setValue(saved.code);
        this.loadCities(saved.code);
      })
    );
  }

  private loadCities(stateCode: string): void {
    if (!stateCode) return;
    this.subscriptions.add(
      this.countryAddress.getLocalGovernments(stateCode).subscribe((cities) => {
        this.cities = cities;
        const control = this.vendorForm.get('townCity');
        control.enable({ emitEvent: false });

        const saved = this.byName(cities, this.dialogData?.vendor?.townCity);
        if (!saved || control.value) return;
        control.setValue(saved.code);
      })
    );
  }

  /** Country changed - the state and city below it no longer apply. */
  public onCountryChange(countryCode: string): void {
    this.states = [];
    this.cities = [];
    this.stateSearch.setValue('');
    this.citySearch.setValue('');
    this.vendorForm.get('state').reset('');
    this.vendorForm.get('townCity').reset('');
    this.vendorForm.get('townCity').disable({ emitEvent: false });
    this.loadStates(countryCode);
  }

  /** State changed - the city below it no longer applies. */
  public onStateChange(stateCode: string): void {
    this.cities = [];
    this.citySearch.setValue('');
    this.vendorForm.get('townCity').reset('');
    this.loadCities(stateCode);
  }

  /* Scoped to the tenant's country like the client bank form, but keyed by the
     real bankCode - that is what the vendor payload stores, alongside the name. */
  private loadBanks(): void {
    this.subscriptions.add(
      this.countryAddress.getTenantBanksWithCode().subscribe((banks) => {
        this.banks = banks;
      })
    );
  }

  /** the payload carries both, so the name follows the selected code */
  public onBankChange(code: string): void {
    const bank = this.banks.find((option) => option.code === code);
    this.vendorForm.get('bankName').setValue(bank?.name ?? '');
  }

  /* ------------------------------------------------------- asset categories */

  public get assetCategories(): string[] {
    return this.vendorForm.get('assetCategories').value ?? [];
  }

  public addAssetCategory(): void {
    const category = (this.newCategoryControl.value || '').trim();
    if (!category) return;
    this.vendorForm.get('assetCategories').setValue([...this.assetCategories, category]);
    this.newCategoryControl.reset('');
  }

  public removeAssetCategory(index: number): void {
    this.vendorForm
      .get('assetCategories')
      .setValue(this.assetCategories.filter((_, categoryIndex) => categoryIndex !== index));
  }

  /* ------------------------------------------------------------------ save */

  public cancel(): void {
    this.dialogRef.close();
  }

  /** the field labels a validation message names, in form order */
  private readonly fieldLabels: { [control: string]: string } = {
    name: $localize`:@@vendor_dealer_name:Vendor/Dealer Name`,
    country: $localize`:@@country:Country`,
    state: $localize`:@@state:State`,
    townCity: $localize`:@@town_city:Town/City`,
    contactPerson: $localize`:@@contact_person:Contact Person`,
    telephone: $localize`:@@telephone:Telephone`,
    email: $localize`:@@email:Email`,
    bankCode: $localize`:@@bank_name:Bank Name`,
    accountNumber: $localize`:@@account_number:Account Number`,
    accountName: $localize`:@@account_name:Account Name`,
  };

  /** true once the field has been touched and is failing a rule */
  public showError(control: string): boolean {
    const field = this.vendorForm.get(control);
    return !!field && field.invalid && (field.touched || field.dirty);
  }

  public errorFor(control: string): string {
    const field = this.vendorForm.get(control);
    const label = this.fieldLabels[control] ?? control;
    if (!field?.errors) return '';
    if (field.errors['required']) {
      return $localize`:@@field_is_required:${label}:label: is required`;
    }
    if (field.errors['email']) {
      return $localize`:@@enter_a_valid_email:Enter a valid email address`;
    }
    if (field.errors['pattern']) {
      return $localize`:@@account_number_ten_digits:Account number must be 10 digits`;
    }
    // Any other key - ngx-intl-tel-input contributes its own - still has to say
    // which field is wrong rather than showing an empty message.
    return $localize`:@@check_this_field:${label}:label: is not valid`;
  }

  public save(): void {
    if (this.vendorForm.invalid) {
      this.vendorForm.markAllAsTouched();
      // Without this the button looks dead - the form simply refuses to submit
      // and nothing on screen says why.
      this.toast.error(this.firstError());
      return;
    }

    const payload = this.buildPayload();
    const id = this.dialogData?.vendor?.id;
    const request = id
      ? this.vendorService.updateVendor(id, payload)
      : this.vendorService.createVendor(payload);

    this.saving = true;
    request.subscribe({
      next: (res: any) => {
        this.saving = false;
        if (!res?.successful) return;
        this.toast.success(
          id
            ? $localize`:@@vendor_updated_successfully:Vendor updated successfully`
            : $localize`:@@vendor_created_successfully:Vendor created successfully`
        );
        // The list reloads from the API, so it only needs to know a save landed.
        this.dialogRef.close(true);
      },
      // The interceptor surfaces the API error; this only releases the button.
      error: () => (this.saving = false),
    });
  }

  /** the message for the first field in form order that is failing */
  private firstError(): string {
    for (const control of Object.keys(this.fieldLabels)) {
      if (this.vendorForm.get(control)?.invalid) return this.errorFor(control);
    }
    return $localize`:@@complete_all_required_fields:Complete all required fields`;
  }

  private buildPayload(): Vendor {
    const value = this.vendorForm.getRawValue();

    return {
      name: value.name,
      businessRegistrationNumber: value.businessRegistrationNumber,
      // The selects hold codes; the API stores the readable names.
      country: this.nameOf(this.countries, value.country),
      state: this.nameOf(this.states, value.state),
      townCity: this.nameOf(this.cities, value.townCity),
      businessAddress: value.businessAddress,
      contactPerson: value.contactPerson,
      telephone: this.phoneNumber(value.telephone),
      email: value.email,
      bankName: value.bankName,
      bankCode: value.bankCode,
      accountNumber: value.accountNumber,
      accountName: value.accountName,
      assetCategories: (value.assetCategories ?? []).map((category: string) =>
        this.categoryValue(category)
      ),
    };
  }

  /** the readable name behind a selected code, or the raw value if unmatched */
  private nameOf(options: AddressOption[], code: string): string {
    if (!code) return '';
    return options.find((option) => option.code === code)?.name ?? code;
  }

  private byName(options: AddressOption[], name?: string): AddressOption | undefined {
    if (!name) return undefined;
    return options.find(
      (option) => option.name?.toLowerCase() === name.toLowerCase()
    );
  }

  /* ngx-intl-tel-input holds an object while the user is typing and a plain
     string once a saved value is patched back in. The API wants the full
     international number either way. */
  private phoneNumber(value: any): string {
    if (typeof value === 'string') return value;
    return (
      value?.e164Number ?? value?.internationalNumber ?? value?.number ?? ''
    );
  }

  /* Categories are typed freehand but stored as enum-style constants
     ("Motor Cycle" -> "MOTOR_CYCLE"). If the backend turns out to accept only a
     fixed set, this becomes a select and this normalizing goes away. */
  private categoryValue(category: string): string {
    return category.trim().toUpperCase().replace(/\s+/g, '_');
  }
}
